package com.neurobs.http

import com.neurobs.ccab.{GeneralResult, ObjectResult}
import com.neurobs.errors.{Result, StringErrorReport, ThrowableErrorReport}
import io.circe.Decoder
import io.circe.generic.auto._

import java.util.concurrent.TimeUnit
import scala.concurrent.{Await, ExecutionContext}
import scala.concurrent.duration.Duration
import scala.util.{Failure, Success, Try}

/**
 * Contains convenience methods for making REST calls that return standard responses
 *
 * @param urlPrefix Used as the beginning of all request URLs
 * @param session Session used to make the HTTP calls
 * @param executor Context for Future handling
 */
class HTTPService(urlPrefix: String, session: Session)(implicit executor: ExecutionContext, urlEncoder: URLUtils) {
	import HTTPService._

	private def makeURL(urlSuffix: String, args: Iterable[(String, Option[Any])] = List()): String = {
		makeArgumentURL(urlPrefix + urlSuffix, args)
	}

	/** Requests that return binary data in the response body (e.g. files)*/
	def binaryResponseCall(urlSuffix: String, urlArguments: Iterable[(String, Option[Any])] = List(), t: HTTPRequestType.Value, body: DataSource = EmptyDataSource): RequestCall[BodyData] = {
		session.call(
			url = makeURL(urlSuffix = urlSuffix, args = urlArguments),
			t = t,
			body = body,
			binaryResponseExpected = true
		)
	}

	/** Requests that return JSON for an object of type T in the response body */
	def objectCall[T](urlSuffix: String, urlArguments: Iterable[(String, Option[Any])] = List(), t: HTTPRequestType.Value, body: DataSource = EmptyDataSource)(implicit d: Decoder[T]): RequestCall[T] = {
		toObjectResponse[T](session.call(
			url = makeURL(urlSuffix = urlSuffix, args = urlArguments),
			t = t,
			body = body
		))
	}

	/** Requests that return JSON for GeneralResult in the response body */
	def generalResultCall(urlSuffix: String, urlArguments: Iterable[(String, Option[Any])] = List(), t: HTTPRequestType.Value, body: DataSource = EmptyDataSource): RequestCall[Unit] = {
		handleGeneralResult(session.call(
			url = makeURL(urlSuffix = urlSuffix, args = urlArguments),
			t = t,
			body = body
		))
	}

	/** Requests that return JSON for ObjectResult[T] in the response body */
	def objectResultCall[T](urlSuffix: String, urlArguments: Iterable[(String, Option[Any])] = List(), t: HTTPRequestType.Value, body: DataSource = EmptyDataSource)(implicit d: Decoder[T]): RequestCall[T] = {
		handleObjectResult[T](session.call(
			url = makeURL(urlSuffix = urlSuffix, args = urlArguments),
			t = t,
			body = body
		))
	}

	/** Requests that return JSON for ObjectResult[T] in the response body and the data member may be empty*/
	def objectResultOptionCall[T](urlSuffix: String, urlArguments: Iterable[(String, Option[Any])] = List(), t: HTTPRequestType.Value, body: DataSource = EmptyDataSource)(implicit d: Decoder[T]): RequestCall[Option[T]] = {
		handleObjectResultOption[T](session.call(
			url = makeURL(urlSuffix = urlSuffix, args = urlArguments),
			t = t,
			body = body
		))
	}

	/** Requests that return no data in the response body */
	def noResultCall(urlSuffix: String, urlArguments: Iterable[(String, Option[Any])] = List(), t: HTTPRequestType.Value, body: DataSource = EmptyDataSource): RequestCall[Unit] = {
		toNoResponse(session.call(
			url = makeURL(urlSuffix = urlSuffix, args = urlArguments),
			t = t,
			body = body
		))
	}
}

object HTTPService {

	def makeArgumentURL(url: String, args: Iterable[(String, Option[Any])] = List())(implicit urlEncoder: URLUtils): String = {
		val strings = args map { a => a._2 match {
			case None => None
			case Some(value) =>
				val encoded = urlEncoder.encode(value.toString)
				Some(s"${a._1}=$encoded")
		}}
		val parameters = strings.flatten.mkString("&")
		if (parameters.isEmpty) url else s"$url?$parameters"
	}

	def handleGeneralResult(callValue: RequestCall[BodyData])(implicit executor: ExecutionContext): RequestCall[Unit] = {
		val temp = toObjectResponse[GeneralResult](callValue)
		(
			temp._1,
			temp._2 map {
				case Left(e) => Left(e)
				case Right(obj) => obj.error match {
					case Some(e) => Left(StringErrorReport(e))
					case None => Right(Unit)
				}
			}
		)
	}

	def handleObjectResult[T](callValue: RequestCall[BodyData])(implicit d: Decoder[T], executor: ExecutionContext): RequestCall[T] = {
		val temp = toObjectResponse[ObjectResult[T]](callValue)
		(
			temp._1,
			temp._2 map { res =>
				res flatMap { obj =>
					obj.error match {
						case Some(e) => Left(StringErrorReport(e))
						case None => obj.data match {
							case Some(t) => Right(t)
							case None => Left(StringErrorReport("ObjectResult with no error and no data."))
						}
					}
				}
			}
		)
	}

	def handleObjectResultOption[T](callValue: RequestCall[BodyData])(implicit d: Decoder[T], executor: ExecutionContext): RequestCall[Option[T]] = {
		val temp = toObjectResponse[ObjectResult[T]](callValue)
		(
			temp._1,
			temp._2 map { res =>
				res flatMap { obj =>
					obj.error match {
						case Some(e) => Left(StringErrorReport(e))
						case None => obj.data match {
							case Some(t) => Right(Some(t))
							case None => Right(None)
						}
					}
				}
			}
		)
	}

	def sync[T](call: RequestCall[T], duration: Duration = Duration(10000, TimeUnit.SECONDS), errorPrefix: String = ""): Result[T] = {
		val res = Try {
			Await.result(call._2, duration)
		} match {
			case Success(value) => value
			case Failure(e) =>Left(ThrowableErrorReport(e))
		}
		if (errorPrefix.isEmpty) res
		else res match {
			case Right(r) => res
			case Left(e) => Left(s"$errorPrefix: ${e.description}")
		}
	}
}